import { z } from 'zod';
import { EmailSendSchema } from './email-sending.js';
import { EmailCalendarSchema } from './email-calender.js';

// ─────────────────────────────────────────────────────────────────
// Workspace API access — key pairs and the scopes they carry
//
// A workspace can hand out API credentials so an external system (a CRM, a
// form handler, a cron job) can drive it without a user session. A
// credential is a *pair*:
//
//   · the **key**    — a public identifier, stored in the clear and shown in
//                      the editor, e.g. `bfk_9f2c…`. It says *which* key is
//                      calling, never authorises anything on its own.
//   · the **secret** — generated with the key, returned exactly once, and
//                      stored only as a bcrypt hash. Losing it means
//                      creating a new key; it can never be read back.
//
// Both travel together on every request (`Authorization: Bearer <key>.<secret>`
// or the `X-Api-Key` / `X-Api-Secret` header pair — see
// {@link parseApiKeyCredentials}), which is what makes a leaked key alone
// useless.
//
// **Scopes are the granularity**: a key may do exactly the actions it was
// granted, and nothing else. There is deliberately no wildcard scope and no
// project-wide "full access" grant — the three actions below are the whole
// surface today, and a key that needed all three would list all three.
//
// The workspace a key acts on is the key's own workspace, so API requests
// never carry `X-Workspace-Id`: a credential cannot be pointed at another
// tenant, and revoking it in one workspace cannot affect another.
// ─────────────────────────────────────────────────────────────────

/**
 * Every action an API key can be granted. Names are `resource:action` so a
 * scope reads as a sentence fragment in a UI checkbox list ("Queue email
 * sends") and stays unambiguous in a log line.
 */
export const API_KEY_SCOPES = [
  'emails:build',
  'emails:send',
  'sends:queue',
  'subscribers:add',
  'subscribers:update',
] as const;

export const ApiKeyScopeSchema = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof ApiKeyScopeSchema>;

/**
 * What each scope grants, in the words the editor shows. Kept next to the
 * enum so a new scope cannot be added without a label, and so the checkbox
 * list, the OpenAPI description and the docs cannot drift from the guard
 * that enforces it.
 */
export const API_KEY_SCOPE_META: Record<
  ApiKeyScope,
  { label: string; description: string; endpoint: string }
> = {
  'emails:build': {
    label: 'Render an email to HTML',
    description:
      'Render one of this workspace’s emails exactly as a send would — theme, design tokens, footer and opt-out link included — and get the finished HTML document back. Nothing is sent, and nothing is published.',
    endpoint: 'POST /api/v1/emails/{emailId}/build',
  },
  'emails:send': {
    label: 'Send an email to one address',
    description:
      'Render one of this workspace’s emails and deliver it straight to a single address — merge values and the recipient in one request, no list and no queue. The delivered copy is a real send: it carries the theme, footer, opt-out link and one-click opt-out headers.',
    endpoint: 'POST /api/v1/emails/{emailId}/send',
  },
  'sends:queue': {
    label: 'Queue email sends',
    description:
      'Queue one of this workspace’s emails to a selection of its lists. Recipients are snapshotted, then delivered by the background sender job.',
    endpoint: 'POST /api/v1/sends',
  },
  'subscribers:add': {
    label: 'Add subscribers to a list',
    description:
      'Add one contact — email, optional name and attribute values — to a list in this workspace.',
    endpoint: 'POST /api/v1/lists/{listId}/subscribers',
  },
  'subscribers:update': {
    label: 'Update subscriber attributes',
    description:
      'Replace one contact’s attribute values (the merge fields a send personalises with).',
    endpoint: 'PUT /api/v1/lists/{listId}/subscribers/{ref}/attributes',
  },
};

export const API_KEY_SCOPE_LABELS: Record<ApiKeyScope, string> = Object.fromEntries(
  API_KEY_SCOPES.map((scope) => [scope, API_KEY_SCOPE_META[scope].label]),
) as Record<ApiKeyScope, string>;

/** Whether a (already validated) scope list grants `scope`. */
export function apiKeyHasScope(scopes: readonly ApiKeyScope[], scope: ApiKeyScope): boolean {
  return scopes.includes(scope);
}

/**
 * Read a stored scopes value tolerantly: keep the entries that are real
 * scopes and drop everything else.
 *
 * This is a security boundary, not just tidiness — the value is jsonb, so a
 * hand-edited row could hold anything. Reading it through this function means
 * an unknown string can never be treated as a grant, and the failure mode of
 * a corrupted row is "this key can do less", never "more".
 */
export function parseApiKeyScopes(raw: unknown): ApiKeyScope[] {
  if (!Array.isArray(raw)) return [];
  const scopes: ApiKeyScope[] = [];
  for (const entry of raw) {
    const parsed = ApiKeyScopeSchema.safeParse(entry);
    if (parsed.success && !scopes.includes(parsed.data)) scopes.push(parsed.data);
  }
  // Ordered by the canonical list, so a response reads the same regardless of
  // the order the grants were picked or stored in.
  return API_KEY_SCOPES.filter((scope) => scopes.includes(scope));
}

// ─────────────────────────────────────────────────────────────────
// Credential format
//
// Fixed prefix + fixed length, which buys three things: a leaked credential
// is recognisable in a log or a paste, the key column can be indexed and
// looked up directly (no scanning every row to find a match), and a scanner
// can reject an obviously malformed credential without a database round trip.
// ─────────────────────────────────────────────────────────────────

/** Marks every API key: `bfk_…`. */
export const API_KEY_PREFIX = 'bfk_';
/** 20 bytes → 40 lowercase hex characters. */
export const API_KEY_HEX_LENGTH = 40;
/** 32 bytes → 64 lowercase hex characters. */
export const API_KEY_SECRET_HEX_LENGTH = 64;

const KEY_PATTERN = new RegExp(`^${API_KEY_PREFIX}[0-9a-f]{${API_KEY_HEX_LENGTH}}$`);
const SECRET_PATTERN = new RegExp(`^[0-9a-f]{${API_KEY_SECRET_HEX_LENGTH}}$`);

/** Whether `value` has the shape of a key (never proof that it exists). */
export function isApiKeyShape(value: string): boolean {
  return KEY_PATTERN.test(value);
}

/** Whether `value` has the shape of a key's secret. */
export function isApiKeySecretShape(value: string): boolean {
  return SECRET_PATTERN.test(value);
}

/**
 * A short, non-reversible label for a key — the first 8 random characters,
 * e.g. `bfk_9f2c1a4b…`. For places that must name a key without printing
 * the whole thing (log lines); the editor shows the key itself, because the
 * key is not a secret.
 */
export function apiKeyHint(key: string): string {
  return `${key.slice(0, API_KEY_PREFIX.length + 8)}…`;
}

/** The header value that carries a key pair: `Bearer <key>.<secret>`. */
export function formatApiKeyAuthorization(key: string, secret: string): string {
  return `Bearer ${key}.${secret}`;
}

export interface ApiKeyCredentials {
  key: string;
  secret: string;
}

/**
 * Read a key pair out of request headers.
 *
 * Two accepted spellings, because both are in the wild and neither is
 * ambiguous:
 *
 *   · `Authorization: Bearer <key>.<secret>` — one header, the shape the
 *     create response hands back ready to paste.
 *   · `X-Api-Key: <key>` + `X-Api-Secret: <secret>` — the explicit pair, for
 *     clients that would rather not build a composite token.
 *
 * Returns null when neither spelling supplies both halves — a half-credential
 * is never "close enough" to authenticate with.
 */
export function parseApiKeyCredentials(headers: {
  authorization?: string | string[] | undefined;
  apiKey?: string | string[] | undefined;
  apiSecret?: string | string[] | undefined;
}): ApiKeyCredentials | null {
  const bearer = firstValue(headers.authorization);
  if (bearer) {
    // `Bearer` is case-insensitive per RFC 6750; the scheme name is not part
    // of the credential, so only the token after it is read.
    const match = /^bearer\s+(.+)$/i.exec(bearer.trim());
    const token = match?.[1]?.trim();
    if (token) {
      // Split on the FIRST dot: a key is hex behind a fixed prefix, so it
      // never contains one, and the remainder is the secret whatever it holds.
      const separator = token.indexOf('.');
      if (separator > 0) {
        const key = token.slice(0, separator);
        const secret = token.slice(separator + 1);
        if (key && secret) return { key, secret };
      }
    }
  }

  const key = firstValue(headers.apiKey)?.trim();
  const secret = firstValue(headers.apiSecret)?.trim();
  if (key && secret) return { key, secret };

  return null;
}

/** Header values may arrive as an array (a repeated header); one value or nothing. */
function firstValue(value: string | string[] | undefined): string | undefined {
  const single = Array.isArray(value) ? value[0] : value;
  return typeof single === 'string' && single !== '' ? single : undefined;
}

// ─────────────────────────────────────────────────────────────────
// Request / response shapes
// ─────────────────────────────────────────────────────────────────

/**
 * One API key as the editor (and the create response) sees it. The secret is
 * absent by construction: it is never stored in a readable form, so there is
 * nothing to omit from this shape — only the create result carries one.
 */
export const ApiKeySchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  /** Human label — "Zapier", "Website signup form". */
  name: z.string(),
  /** The public identifier. Safe to log and to display. */
  key: z.string(),
  scopes: z.array(ApiKeyScopeSchema),
  /** Who created it (null once that account is deleted). */
  createdBy: z.string().nullable(),
  createdByEmail: z.string().nullable(),
  /** When the key stops working, or null for "until revoked". */
  expiresAt: z.string().nullable(),
  /** Last request authenticated with this key; null while unused. */
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  /** False once revoked or expired — the state that matters to a caller. */
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type ApiKey = z.infer<typeof ApiKeySchema>;

export const ApiKeyListSchema = z.object({ data: z.array(ApiKeySchema) });
export type ApiKeyList = z.infer<typeof ApiKeyListSchema>;

export const CreateApiKeySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(80),
  /** At least one: a key that may do nothing is a mistake, not a setting. */
  scopes: z
    .array(ApiKeyScopeSchema)
    .min(1, 'Pick at least one permission')
    .max(API_KEY_SCOPES.length)
    .refine((scopes) => new Set(scopes).size === scopes.length, 'Duplicate permission'),
  /** Optional expiry (ISO instant); null/omitted means it never expires. */
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export type CreateApiKeyInput = z.infer<typeof CreateApiKeySchema>;

/**
 * What creating a key returns. `secret` and `authorization` are present in
 * this response and nowhere else, ever — the caller has to store them.
 */
export const CreateApiKeyResultSchema = z.object({
  apiKey: ApiKeySchema,
  secret: z.string(),
  /** `Bearer <key>.<secret>` — paste straight into an Authorization header. */
  authorization: z.string(),
});
export type CreateApiKeyResult = z.infer<typeof CreateApiKeyResultSchema>;

/** Who an API request is acting as, for `GET /api/v1/me`. */
export const ApiAccessIdentitySchema = z.object({
  workspace: z.object({ id: z.string().uuid(), name: z.string() }),
  key: z.object({
    id: z.string().uuid(),
    name: z.string(),
    scopes: z.array(ApiKeyScopeSchema),
  }),
});
export type ApiAccessIdentity = z.infer<typeof ApiAccessIdentitySchema>;

// ── Rendering an email (scope `emails:build`) ────────────────────

/**
 * Body of `POST /api/v1/emails/{emailId}/build`. Everything is optional: with
 * an empty body the render uses the email's own variable defaults, exactly as
 * the editor's Preview does.
 */
export const ApiEmailBuildRequestSchema = z.object({
  /**
   * Merge values for this render. Layered over the email's variable defaults,
   * so a caller only has to send what it wants to differ. Checked against the
   * email's definitions — a wrong type or a missing required variable is a
   * 422 rather than an email full of empty merge fields.
   */
  variables: z.record(z.string(), z.unknown()).optional(),
  /**
   * The address this copy is for. Only the opt-out link differs between two
   * copies of one email, and the link cannot name a recipient the render does
   * not know — so pass this to see the link a specific person would receive.
   * Omitted leaves the placeholder a send fills in per delivery.
   */
  recipient: z.string().email('Invalid recipient address').optional(),
});
export type ApiEmailBuildRequest = z.infer<typeof ApiEmailBuildRequestSchema>;

/**
 * The rendered email. `html` is the finished document — the same bytes the
 * sender would deliver — so it can be stored, diffed, sent through a
 * provider of the caller's own, or rendered in a browser to look at.
 *
 * There is deliberately no public URL: a render does not publish anything
 * (the editor's Preview writes into the served output directory; an API key
 * must not be able to put a draft campaign on a public path).
 */
export const ApiEmailBuildResultSchema = z.object({
  emailId: z.string(),
  subject: z.string(),
  preheader: z.string(),
  /** The complete email document. */
  html: z.string(),
  wordCount: z.number().int(),
  /** Content column width the theme rendered at, in px. */
  width: z.number().int(),
});
export type ApiEmailBuildResult = z.infer<typeof ApiEmailBuildResultSchema>;

// ── Sending one copy directly (scope `emails:send`) ──────────────

/**
 * Body of `POST /api/v1/emails/{emailId}/send`: everything one delivery needs,
 * in one request — the address, the merge values for it, and which sender it
 * goes out as.
 *
 * This is the transactional shape of a send: no audience, no queue, no
 * snapshot. What it does *not* accept is SMTP credentials — the sender is one
 * already configured for the workspace (`POST /api/emails/senders`), so a
 * secret never travels in a request body and never reaches an API client.
 */
export const ApiDirectSendRequestSchema = z.object({
  /** The address this copy goes to. */
  to: z.string().email('Invalid destination address'),
  /**
   * A stored sender of this workspace. Only SMTP senders deliver today.
   */
  senderId: z.string().uuid('Invalid sender id'),
  /**
   * Merge values for this copy, layered over the email's variable defaults.
   * Types are checked; a *required* variable this leaves unfilled is a 422 —
   * unlike a render, a delivery has nowhere to put a blank field.
   */
  variables: z.record(z.string(), z.unknown()).optional(),
  calendarInvite: EmailCalendarSchema.nullable().optional(),
  /** Display name override. Defaults to the sender's own. */
  fromName: z.string().trim().max(100).optional(),
  /** From address override. Defaults to the sender's own. */
  fromAddress: z.string().email('Invalid From address').optional(),
});
export type ApiDirectSendRequest = z.infer<typeof ApiDirectSendRequestSchema>;

/**
 * What the delivery reported back. `accepted` is the SMTP server's own answer
 * — the address it took responsibility for — so a non-empty `accepted` with an
 * empty `rejected` is a send this deployment handed over successfully. It is
 * not a delivery receipt: a bounce after that point belongs to the mailbox
 * provider, and a queued send's bounces appear in its recipient list instead.
 */
export const ApiDirectSendResultSchema = z.object({
  emailId: z.string(),
  to: z.string(),
  /** The effective `From` header, after any override — `Name <addr>` or just the address. */
  from: z.string(),
  subject: z.string(),
  messageId: z.string(),
  accepted: z.array(z.string()),
  rejected: z.array(z.string()),
  /** The SMTP server's final response line. */
  response: z.string(),
});
export type ApiDirectSendResult = z.infer<typeof ApiDirectSendResultSchema>;

// ── Sending from an uploaded CSV (scope `sends:queue`) ───────────

/**
 * The non-file fields of `POST /api/v1/sends/csv` (multipart/form-data).
 *
 * The audience arrives as a file, so the ids and overrides travel as form
 * fields beside it — and, being form fields, they arrive as strings; the route
 * coerces and validates them with this schema rather than trusting the shape
 * a client happened to send.
 */
export const CsvSendFieldsSchema = z.object({
  /** The email to send. */
  emailId: z.string().min(1, 'emailId is required'),
  /** The stored sender it goes out as. Only SMTP senders deliver today. */
  senderId: z.string().uuid('Invalid sender id'),
  /** ISO instant, or omitted for "as soon as the sender job runs". */
  scheduledAt: z.string().datetime({ offset: true }).nullable().optional(),
  fromName: z.string().trim().max(100).optional(),
  fromAddress: z.string().email('Invalid From address').optional(),
  /** Where replies go, when the From address is not a mailbox anyone reads. */
  replyTo: z.string().email('Invalid reply-to address').optional(),
  /** True when the file's first row is a header. Auto-detected when omitted. */
  hasHeader: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((value) => value === true || value === 'true')
    .optional(),
});
export type CsvSendFields = z.infer<typeof CsvSendFieldsSchema>;

/** One row the file could not contribute, and why. */
export const CsvSendRowErrorSchema = z.object({
  /** 1-based line in the file, header included. */
  row: z.number().int(),
  email: z.string().nullable(),
  reason: z.enum([
    'missing_email',
    'invalid_email',
    'too_long',
    'invalid_attribute',
    'missing_required',
    'duplicate_email',
  ]),
  /** Detail for the reason — which variable, and what was wrong with it. */
  message: z.string().optional(),
});
export type CsvSendRowError = z.infer<typeof CsvSendRowErrorSchema>;

/**
 * The audience a CSV send built, and what it had to leave out.
 *
 * A CSV send is **all or nothing**: every data row must carry a usable email
 * address and a value for each of the email's required variables, or the whole
 * request is a 422 (see `errors`) and nothing is queued. An import can afford
 * to skip a bad row and report it; a delivery to real people cannot — there is
 * no second pass once the mail is out.
 */
export const CsvSendResultSchema = z.object({
  /** The queued send, with `csvFilename` set. */
  send: EmailSendSchema,
  /** Distinct addresses queued. */
  recipientCount: z.number().int(),
  /** Addresses the snapshot already held (a re-send), queued once. */
  duplicates: z.number().int(),
  /** Data rows in the file, header excluded. */
  totalRows: z.number().int(),
  /** Rows rows that became recipients (after de-duplication). */
  accepted: z.number().int(),
  /**
   * The audience the rows were imported into. Temporary by construction: the
   * cleanup job deletes it once this send finishes.
   */
  audience: z.object({
    id: z.string().uuid(),
    name: z.string(),
    temporary: z.literal(true),
  }),
  /** In-file duplicates that were collapsed to one address. */
  duplicatesInFile: z.number().int(),
  /** Which file column fed each variable, as detected from the header. */
  columns: z.record(z.string(), z.number().int()),
});
export type CsvSendResult = z.infer<typeof CsvSendResultSchema>;

/** True while a key row still authenticates: neither revoked nor expired. */
export function isApiKeyActive(
  key: { revokedAt: string | null; expiresAt: string | null },
  now: number = Date.now(),
): boolean {
  if (key.revokedAt) return false;
  if (key.expiresAt && new Date(key.expiresAt).getTime() <= now) return false;
  return true;
}
